import { Button, Tooltip } from "@hellocarbo/lasagne";
import { Plus, Trash2 } from "lucide-react";
import { use } from "react";
import { useTranslation } from "react-i18next";
import { useFetcher } from "react-router";

import { PortalContainerContext } from "@/contexts";
import { TTrajectoryAction } from "@/types";

import ActionDialog from "./ActionDialog";

interface ActionListItemProps
  extends Omit<
    TTrajectoryAction,
    | "id"
    | "name_fr"
    | "name_en"
    | "content_fr"
    | "content_en"
    | "is_intensity_editable"
  > {
  name: string;
  content: string;
  isReadOnly?: boolean;
  actionId: number;
  intensityIsEditable: boolean;
}

function ActionListItem({
  name,
  content,
  category,
  recommended,
  reduction_factor,
  examples,
  isReadOnly = true,
  status,
  actionId,
  difficulty,
  intensity_percentage,
  reduction_tco2e,
  intensityIsEditable,
}: ActionListItemProps) {
  const { container } = use(PortalContainerContext);

  const { t } = useTranslation(["formatter", "trajectory"]);
  const fetcher = useFetcher();

  const isAvailable = status === "available";

  return (
    <li className="gap-x-xs flex w-full items-center">
      <ActionDialog
        name={name}
        content={content}
        recommended={recommended}
        category={category}
        status={status}
        difficulty={difficulty}
        examples={examples}
        intensity_percentage={intensity_percentage}
        reduction_factor={reduction_factor}
        reduction_tco2e={reduction_tco2e}
        isReadOnly={isReadOnly}
        actionId={actionId}
        intensityIsEditable={intensityIsEditable}
      />
      {!isReadOnly && (
        <Tooltip
          portalContainer={container as Element}
          content={t(
            `trajectory:actionPlan.${isAvailable ? "addAction" : "removeAction"}`
          )}
          triggerAsChild
        >
          <Button
            size="small"
            shape="soft"
            intent={isAvailable ? "positive" : "negative"}
            Icon={isAvailable ? Plus : Trash2}
            onClick={() =>
              fetcher.submit(
                {
                  action: isAvailable ? "add" : "remove",
                  actionId: actionId,
                },
                { method: "PATCH" }
              )
            }
            isLoading={fetcher.state === "submitting"}
            disabled={fetcher.state !== "idle"}
          />
        </Tooltip>
      )}
    </li>
  );
}

export default ActionListItem;
